import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../../api/auth';
import Button from '../shared/Button';

const AGREEMENT_TEXT = `Соглашение между спортсменом и спортивным клубом Leikin Team:
1. Спортсмен получает тренерское сопровождение и неограниченную возможность общения. Получение обратной связи от тренера не позднее чем 48 часов с момента обращения.
2. В зависимости от оплаченного тренировочного пакета спортсмен получает тренировочный план и/или имеет возможность присоединяться на групповые тренировки и командные мероприятия.
3. Спортсмен получает возможность пользоваться командными скидками и бонусами на покупку инвентаря, регистрацию на соревнования и участие в тренировочных сборах и мероприятиях.
4. Спортсмен получает возможность пользоваться услугами командного менеджера в помощи по решению бюрократических вопросов, связанных с их спортивной деятельностью.
5. Спортсмен получает возможность пользоваться медиа (фото и видео) материалами, сделанными на групповых мероприятиях и тренировках.
6. Спортсмен обязуется давать обратную связь тренеру минимум раз в неделю и по необходимости, при возникновении непредвиденных обстоятельств.
7. Спортсмен обязуется выполнять тренировочную программу в полном объеме. Если выполнение невозможно, необходимо сообщить об этом тренеру для корректировки программы.
8. Спортсмен обязуется ежемесячно производить оплату путем автоматического платежа через платежную систему.
9. Оплата производится помесячно (как абонемент). За единичные отмены тренировок возврат средств не предусмотрен.
10. В случае прекращения тренировок спортсмен обязан заранее сообщить об этом тренеру или менеджеру до начала нового месяца.`;

const parseHeightWeight = (raw) => {
  const parts = String(raw || '').match(/\d+/g) || [];
  return { height: parts[0] || '', weight: parts[1] || '' };
};

const RegistrationFormModal = ({ isOpen, onClose, onComplete, initialData, prefillData, required = false }) => {
  const [loading, setLoading] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    phone_number: '',
    date_of_birth: '',
    height_cm: '',
    weight_kg: '',
    coach_agreement_accepted: false,
    health_responsibility_accepted: false,
  });

  useEffect(() => {
    if (!isOpen) return;
    const hw = parseHeightWeight(initialData?.height_weight);
    const fullName = String(initialData?.full_name || '').trim().split(/\s+/).filter(Boolean);
    setFormData({
      first_name: fullName[0] || prefillData?.first_name || '',
      last_name: fullName.slice(1).join(' ') || prefillData?.last_name || '',
      gender: initialData?.gender || prefillData?.gender || '',
      phone_number: initialData?.phone_number || prefillData?.phone_number || '',
      date_of_birth: initialData?.date_of_birth || prefillData?.date_of_birth || '',
      height_cm: hw.height || '',
      weight_kg: hw.weight || '',
      coach_agreement_accepted: Boolean(initialData?.coach_agreement_accepted),
      health_responsibility_accepted: Boolean(initialData?.health_responsibility_accepted),
    });
  }, [initialData, isOpen, prefillData]);

  if (!isOpen) return null;

  const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const requiredFields = ['first_name', 'last_name', 'gender', 'phone_number', 'date_of_birth', 'height_cm', 'weight_kg'];
    const missing = requiredFields.some((field) => !String(formData[field] || '').trim());
    if (missing) {
      toast.error('Заполните все обязательные поля анкеты атлета');
      return false;
    }
    if (!formData.coach_agreement_accepted || !formData.health_responsibility_accepted) {
      toast.error('Подтвердите оба обязательных согласия');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      await authAPI.updateHealthForm({
        full_name: `${formData.first_name} ${formData.last_name}`.trim(),
        gender: formData.gender,
        phone_number: formData.phone_number,
        date_of_birth: formData.date_of_birth,
        height_weight: `${formData.height_cm} / ${formData.weight_kg}`,
        coach_agreement_accepted: formData.coach_agreement_accepted,
        health_responsibility_accepted: formData.health_responsibility_accepted,
      });
      toast.success('Анкета атлета сохранена');
      if (onComplete) {
        await Promise.resolve(onComplete());
      }
      if (!required && onClose) onClose();
    } catch (error) {
      toast.error('Не удалось сохранить анкету атлета');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto relative shadow-2xl">
        {!required && (
          <button onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-gray-100 rounded-full transition-colors z-10">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}
        <div className="bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] p-4 sm:p-6 text-white rounded-t-2xl sm:rounded-t-3xl">
          <h2 className="text-xl sm:text-2xl font-bold">Анкета атлета</h2>
          <p className="text-xs sm:text-sm mt-1 text-white/90">Обязательно заполните перед переходом к медицинской анкете</p>
        </div>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className="border rounded-lg px-3 py-2.5" placeholder="Имя *" value={formData.first_name} onChange={(e) => setField('first_name', e.target.value)} />
            <input className="border rounded-lg px-3 py-2.5" placeholder="Фамилия *" value={formData.last_name} onChange={(e) => setField('last_name', e.target.value)} />
            <select className="border rounded-lg px-3 py-2.5" value={formData.gender} onChange={(e) => setField('gender', e.target.value)}>
              <option value="">Пол *</option>
              <option value="male">М</option>
              <option value="female">Ж</option>
              <option value="other">Другое</option>
            </select>
            <input className="border rounded-lg px-3 py-2.5" placeholder="Телефон *" value={formData.phone_number} onChange={(e) => setField('phone_number', e.target.value.replace(/\D/g, '').slice(0, 15))} />
            <input type="date" className="border rounded-lg px-3 py-2.5" value={formData.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <input className="border rounded-lg px-3 py-2.5" placeholder="Рост *" value={formData.height_cm} onChange={(e) => setField('height_cm', e.target.value.replace(/\D/g, '').slice(0, 3))} />
              <input className="border rounded-lg px-3 py-2.5" placeholder="Вес *" value={formData.weight_kg} onChange={(e) => setField('weight_kg', e.target.value.replace(/\D/g, '').slice(0, 3))} />
            </div>
          </div>
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={formData.coach_agreement_accepted} onChange={(e) => setField('coach_agreement_accepted', e.target.checked)} />
              <span>
                Принимаю
                {' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAgreement(true);
                  }}
                  className="text-[#1f3f95] font-semibold underline underline-offset-2"
                >
                  соглашение с клубом Leikin Team
                </button>
                {' '}
                *
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={formData.health_responsibility_accepted} onChange={(e) => setField('health_responsibility_accepted', e.target.checked)} />
              <span>Подтверждаю личную ответственность за здоровье *</span>
            </label>
          </div>
          <div className="pt-1">
            <Button type="submit" disabled={loading} className="w-full" withArrow>
              {loading ? 'Сохранение...' : 'Сохранить анкету атлета'}
            </Button>
          </div>
        </form>
      </div>

      {showAgreement && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Соглашение Leikin Team</h3>
              <button
                type="button"
                onClick={() => setShowAgreement(false)}
                className="p-1 rounded hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              <p className="text-sm text-gray-700 whitespace-pre-line leading-6">{AGREEMENT_TEXT}</p>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
              <Button type="button" onClick={() => setShowAgreement(false)} className="px-5">
                Закрыть
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationFormModal;
